// src/components/fraud/FraudLogDrawer.js
// Right-side investigation drawer — slides in when a row is clicked.
// Displays ALL available fields from the fraud_log document.
// Read-only. No backend calls.

import React, { useState, useCallback } from 'react';
import { parseDate } from '../../hooks/useFraudLogs';

// ─── Colour maps ──────────────────────────────────────────────────────────────
const ACTION_COLOR = {
  ALLOW: '#22C55E',
  LOG: '#D9FF1F',
  REVIEW: '#F59E0B',
  BLOCK: '#EF4444',
};
const RISK_COLOR = {
  normal: '#22C55E',
  elevated: '#F59E0B',
  highest: '#EF4444',
};

// ─── Copy-to-clipboard mini hook ─────────────────────────────────────────────
const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  }, []);
  return { copied, copy };
};

// ─── Small helper components ──────────────────────────────────────────────────

const CopyBtn = ({ text, label, copied, onCopy }) => {
  const isCopied = copied === text;
  return (
    <button
      title={isCopied ? 'Copied!' : `Copy ${label || ''}`}
      onClick={() => onCopy(text, text)}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 4px',
        cursor: 'pointer',
        color: isCopied ? '#22C55E' : 'var(--text-secondary)',
        fontSize: 12,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    >
      {isCopied ? '✓' : '⧉'}
    </button>
  );
};

const Field = ({ label, value, mono = false, copyable = false, copy, copied }) => {
  if (value === null || value === undefined || value === '') return null;
  const str = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: 'var(--text-secondary)',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 4,
          color: 'var(--text-primary)',
          fontSize: 13,
          fontFamily: mono ? 'monospace' : 'inherit',
          wordBreak: 'break-all',
        }}
      >
        <span style={{ flex: 1 }}>{str}</span>
        {copyable && copy && (
          <CopyBtn text={str} label={label} copied={copied} onCopy={copy} />
        )}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1,
      color: 'var(--accent-lime)',
      borderBottom: '1px solid var(--divider)',
      paddingBottom: 6,
      marginBottom: 14,
      marginTop: 20,
    }}
  >
    {children}
  </div>
);

const ActionBadge = ({ action }) => {
  const upper = (action || '').toUpperCase();
  const color = ACTION_COLOR[upper] || 'var(--text-secondary)';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: upper === 'LOG' ? '#050014' : color,
        background: `${color}22`,
        border: `1px solid ${color}55`,
        borderRadius: 20,
        padding: '3px 10px',
        textTransform: 'uppercase',
      }}
    >
      {action || '—'}
    </span>
  );
};

const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>;
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  return (
    <span style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>
      {score}
      <small style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4 }}>
        / 100
      </small>
    </span>
  );
};

const RiskBadge = ({ level }) => {
  const l = (level || '').toLowerCase();
  const color = RISK_COLOR[l] || 'var(--text-secondary)';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color,
        background: `${color}18`,
        border: `1px solid ${color}44`,
        borderRadius: 20,
        padding: '3px 10px',
        textTransform: 'capitalize',
      }}
    >
      {level || '—'}
    </span>
  );
};

const ReasonChips = ({ reasons }) => {
  if (!reasons || reasons.length === 0) {
    return (
      <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontStyle: 'italic' }}>
        No reasons
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {reasons.map((r, i) => (
        <span
          key={i}
          style={{
            fontSize: 11,
            color: '#F59E0B',
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 4,
            padding: '2px 8px',
          }}
        >
          {r}
        </span>
      ))}
    </div>
  );
};

// ─── Raw JSON viewer ──────────────────────────────────────────────────────────
const RawViewer = ({ data }) => {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--divider)',
          borderRadius: 6,
          color: 'var(--text-secondary)',
          fontSize: 11,
          padding: '4px 10px',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {open ? '▲ Hide raw document' : '▼ Show raw document'}
      </button>
      {open && (
        <pre
          style={{
            marginTop: 8,
            background: 'var(--bg-primary)',
            border: '1px solid var(--divider)',
            borderRadius: 8,
            padding: 12,
            fontSize: 11,
            color: 'var(--text-secondary)',
            overflowX: 'auto',
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const FraudLogDrawer = ({ log, open, onClose }) => {
  const { copied, copy } = useCopy();

  if (!log) return null;

  const stripe = log.stripeRiskData || {};
  const fmtDate = (d) => {
    const date = d instanceof Date ? d : parseDate(d);
    if (!date) return '—';
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1040,
          opacity: open ? 1 : 0,
          transition: 'opacity 0.25s ease',
          pointerEvents: open ? 'all' : 'none',
        }}
      />

      {/* ── Drawer panel ── */}
      <div
        id="fraud-log-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(520px, 92vw)',
          background: 'var(--bg-surface)',
          borderLeft: '1px solid var(--divider)',
          zIndex: 1050,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--divider)',
            flexShrink: 0,
          }}
        >
          <div>
            <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: 15 }}>
              Fraud Log Details
            </h6>
            <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
              ID: {log._id}
            </small>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--divider)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* ── Overview ── */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>
                Fraud Score
              </div>
              <ScoreBadge score={log.score} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>
                Action
              </div>
              <ActionBadge action={log.action} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>
                Risk Level
              </div>
              <RiskBadge level={stripe.riskLevel || stripe.risk_level} />
            </div>
          </div>

          <SectionTitle>Timestamps</SectionTitle>
          <Field label="Created At" value={fmtDate(log.createdAt)} />
          <Field label="Phase" value={log.phase} />
          <Field label="Status" value={log.status} />

          <SectionTitle>User</SectionTitle>
          <Field label="UID" value={log.uid} mono copyable copy={copy} copied={copied} />
          <Field label="Email" value={log.userEmail} copyable copy={copy} copied={copied} />

          <SectionTitle>Transaction</SectionTitle>
          <Field label="Transaction ID" value={log.transactionId} mono copyable copy={copy} copied={copied} />
          <Field label="Payment Intent" value={log.paymentIntent} mono copyable copy={copy} copied={copied} />
          <Field label="Destination" value={log.destination} mono copyable copy={copy} copied={copied} />
          <Field label="Payment Method" value={log.paymentMethod} />

          <SectionTitle>Fraud Reasons</SectionTitle>
          <ReasonChips reasons={log.reasons} />

          {/* ── Stripe Risk Data ── */}
          {Object.keys(stripe).length > 0 && (
            <>
              <SectionTitle>Stripe Risk Data</SectionTitle>
              <Field label="Risk Level" value={stripe.riskLevel || stripe.risk_level} />
              <Field label="Risk Score" value={stripe.riskScore ?? stripe.risk_score} />
              <Field label="Seller Message" value={stripe.sellerMessage || stripe.seller_message} />
              <Field label="Card Brand" value={stripe.cardBrand || stripe.card_brand} />
              <Field label="Card Country" value={stripe.cardCountry || stripe.card_country} />
              <Field label="Card Fingerprint" value={stripe.cardFingerprint || stripe.card_fingerprint} mono />
              <Field label="Network Status" value={stripe.networkStatus || stripe.network_status} />
            </>
          )}

          {/* ── Fraud Details ── */}
          {log.fraudDetails && (
            <>
              <SectionTitle>Fraud Details</SectionTitle>
              {typeof log.fraudDetails === 'object' ? (
                Object.entries(log.fraudDetails).map(([k, v]) => (
                  <Field key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                ))
              ) : (
                <Field label="Details" value={String(log.fraudDetails)} />
              )}
            </>
          )}

          {/* ── Metadata ── */}
          {log.metadata && (
            <>
              <SectionTitle>Metadata</SectionTitle>
              {typeof log.metadata === 'object' ? (
                Object.entries(log.metadata).map(([k, v]) => (
                  <Field key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                ))
              ) : (
                <Field label="Metadata" value={String(log.metadata)} />
              )}
            </>
          )}

          {/* ── Raw document ── */}
          <SectionTitle>Raw Document</SectionTitle>
          <RawViewer data={log.rawDoc} />

          <div style={{ height: 32 }} />
        </div>
      </div>
    </>
  );
};

export default FraudLogDrawer;
